import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchOrders } from '../api/appmanageOrders';
import { getAdminSocket } from '../api/socket';
import { Order } from '../types/admin';

const POLL_INTERVAL_MS = 5000;

const playBeep = () => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = new AudioContextClass();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.4);
    oscillator.onended = () => ctx.close();
  } catch {
    // 오디오 재생을 지원하지 않는 환경이면 조용히 무시
  }
};

export function useOrderPolling(status?: Order['status']) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const knownIds = useRef<Set<number> | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchOrders(status);
      const currentIds = new Set(data.map((o) => o.id));

      if (knownIds.current) {
        const hasNewOrder = data.some((o) => !knownIds.current!.has(o.id));
        if (hasNewOrder) playBeep();
      }
      knownIds.current = currentIds;

      setOrders(data);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    knownIds.current = null;
    load();
    // 폴링은 그대로 두고(소켓 연결이 끊겨도 최신 상태를 보장하는 fallback),
    // 소켓 이벤트가 오면 다음 폴링을 기다리지 않고 즉시 갱신 + 신규 주문 알림음을 울린다.
    const timer = setInterval(load, POLL_INTERVAL_MS);

    const socket = getAdminSocket();
    const onOrderNew = () => {
      playBeep();
      load();
    };
    const onOrderStatusChanged = () => load();

    socket.on('order:new', onOrderNew);
    socket.on('order:status_changed', onOrderStatusChanged);

    return () => {
      clearInterval(timer);
      socket.off('order:new', onOrderNew);
      socket.off('order:status_changed', onOrderStatusChanged);
    };
  }, [load]);

  return { orders, loading, refetch: load };
}
